import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChatSocket } from "../context/ChatSocketContext";
import { tournamentKeys } from "./queries/useTournament";
import { setPlayerElo } from "../util/HelperUtils";
import type { TournamentDetail, TournamentRound } from "../types/domain";

interface TournamentEvent {
  type: "ROUND_STARTED" | "MATCH_UPDATED" | "TOURNAMENT_STATE_CHANGED";
  roundOrder?: number;
  round?: TournamentRound;
  state?: string;
}

/** Mirror of api/tournaments enrichWithLadderElo, applied to a single pushed round. */
const enrichRound = (round: TournamentRound, ladderId: number): TournamentRound => {
  round.scores.forEach((score) => setPlayerElo(score.player, ladderId));
  round.matches.forEach((match) => {
    setPlayerElo(match.playerOne, ladderId);
    setPlayerElo(match.playerTwo, ladderId);
  });
  return round;
};

/**
 * Subscribes to live tournament-detail updates (see backend TournamentEventPublisher) and keeps
 * the cached tournament fresh without any refresh. Updates go through the TanStack Query cache
 * only, so they never close an open score modal or otherwise interrupt the user.
 *
 * onNewRound fires when a new round is announced — the caller decides how to surface it (e.g. a
 * non-intrusive alert with a jump button), rather than auto-navigating mid-interaction.
 */
export const useTournamentRealtime = (
  tournamentId: number | null | undefined,
  onNewRound?: (roundOrder: number) => void,
) => {
  const queryClient = useQueryClient();
  const { subscribe, isConnected } = useChatSocket();

  // Keep the latest callback without forcing a re-subscribe when its identity changes.
  const onNewRoundRef = useRef(onNewRound);
  onNewRoundRef.current = onNewRound;

  // On every (re)connect, refetch so we catch up on anything missed while the socket was down.
  useEffect(() => {
    if (!isConnected || tournamentId == null) return;
    queryClient.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
  }, [isConnected, queryClient, tournamentId]);

  useEffect(() => {
    if (tournamentId == null) return;
    const key = tournamentKeys.detail(tournamentId);
    const unsubscribe = subscribe(`/topic/tournament/${tournamentId}`, (raw) => {
      const event = raw as TournamentEvent;

      // Frequent case: a score changed. Patch just the affected round into the cached tournament
      // instead of refetching the whole (large) payload. Non-disruptive — open modals stay put.
      if (event.type === "MATCH_UPDATED" && event.round) {
        const incoming = event.round;
        const patched = queryClient.setQueryData<TournamentDetail>(key, (old) => {
          if (!old) return old; // nothing cached yet; the next load will fetch fresh
          const round = enrichRound(incoming, old.ladder.id);
          const hasRound = old.rounds.some((r) => r.id === round.id);
          if (!hasRound) return old; // unknown round (shouldn't happen) — leave cache untouched
          return { ...old, rounds: old.rounds.map((r) => (r.id === round.id ? round : r)) };
        });
        // Round wasn't in cache (e.g. brand-new one) — fall back to a full refresh so nothing is missed.
        if (patched && !patched.rounds.some((r) => r.id === incoming.id)) {
          queryClient.invalidateQueries({ queryKey: key });
        }
        return;
      }

      // Structural / rare changes (new round, tournament finished): a full refresh is simplest.
      queryClient.invalidateQueries({ queryKey: key });
      if (event.type === "ROUND_STARTED" && typeof event.roundOrder === "number") {
        onNewRoundRef.current?.(event.roundOrder);
      }
    });
    return unsubscribe;
  }, [subscribe, queryClient, tournamentId]);
};
